/**
 * Compare Bar — 商品比較浮動列
 *
 * 載入：由 /src/main.js 中央 import 鏈 auto-init 第一個 [data-compare-bar]（前台 products 頁）；
 *   另掛 window.CompareBar 供程式式呼叫。
 *
 * TODO(backend): 比較清單存 localStorage（key: compare-bar:items）；接後端時可改伺服器持久化 / 同步會員。
 *
 * 功能：
 *   - listen 全域 [data-add-compare] click 加入 product
 *   - 渲染 slot 縮圖（從觸發 button 所屬卡片 [data-product-id] 抓 title + img，
 *     兼容 .catalog-card / 舊 .product-card markup）
 *   - 點 slot remove 移除 / 點清除全清
 *   - localStorage 持久化（key: compare-bar:items）
 *   - 加入後卡片 [data-product-id] 加 .is-selected
 *   - empty → 自動 hidden；has-items → 顯示
 *
 * API:
 *   import { CompareBar } from './components/compare-bar.js';
 *   const bar = CompareBar.init('[data-compare-bar]');
 *   bar.add(productId);
 *   bar.remove(productId);
 *   bar.clear();
 *   bar.getItems();  // [{ id, title, img }]
 *
 * 全域 auto-init：頁面載入時自動 init 第一個 [data-compare-bar]
 */

import { escapeHtml } from '../utils/dom.js';

// 單一真相源：比較清單 localStorage key。product-compare.js 直接 import 此常數讀同一份選取，
// 不在兩處各寫字面值（避免漂移 → 一邊改 key 另一邊抓不到選取）。
// TODO(backend): （選配）比較清單改伺服器持久化 / 會員同步（目前 localStorage 即可） — 見 docs/BACKEND_HANDOFF.md#compare-bar-persist
export const STORAGE_KEY = 'compare-bar:items';
// 比較上限（單一真相源；markup slot 數與 product-compare 假資料筆數皆對齊此值）。
const MAX_SLOTS = 3;

class CompareBarInstance {
  constructor(el) {
    this.el = el;
    this.slotsEl = el.querySelector('.compare-bar__slots');
    this.clearBtn = el.querySelector('.compare-bar__clear');
    this.cta = el.querySelector('.compare-bar__compare-cta');
    this.maxSlots = parseInt(el.dataset.maxSlots, 10) || MAX_SLOTS;
    this.items = this._load();

    this._bind();
    this._render();
  }

  /* ===== Storage ===== */

  _load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.slice(0, this.maxSlots) : [];
    } catch {
      return [];
    }
  }

  _save() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
    } catch {
      /* ignore */
    }
  }

  /* ===== Bindings ===== */

  _bind() {
    // 全域代理 [data-add-compare] click
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-add-compare]');
      if (!trigger) return;
      const card = trigger.closest('[data-product-id]');
      if (!card) return;
      e.preventDefault();
      const id = card.dataset.productId;
      // 同類限定守衛：跨類「加入」時攔截開警告 modal（移除動作永遠放行 → has(id) 為真者不進此分支）。
      const cat = card.closest('[data-category]')?.dataset.category || '';
      const locked = this.lockedCategory;
      if (!this.has(id) && locked && cat && cat !== locked) {
        window.Modal?.open('#compare-category-modal');
        this._announce('無法加入：比較僅限同類型產品');
        return;
      }
      if (this.has(id)) {
        this.remove(id);
      } else if (this.items.length >= this.maxSlots) {
        // 已達上限且點的是「尚未加入」卡：把該鈕設 aria-disabled（button.css 既有
        // [aria-disabled=true]{opacity:.5;pointer-events:none} 接手視覺 + 擋後續點擊），
        // 並走 add() 的 full 分支觸發 is-full-flash + aria-live 朗讀上限告知。
        // 不做 replace-oldest；已加入卡（button--added）不在此分支 → 移除永遠可用。
        trigger.setAttribute('aria-disabled', 'true');
        this.add(id, this._extractMeta(card));
      } else {
        const meta = this._extractMeta(card);
        this.add(id, meta);
      }
    });

    // 清除按鈕
    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', () => this.clear());
    }

    // slot remove（事件代理）
    if (this.slotsEl) {
      this.slotsEl.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('.compare-bar__slot-remove');
        if (!removeBtn) return;
        const id = removeBtn.dataset.id;
        if (id) this.remove(id);
      });
    }
  }

  _extractMeta(card) {
    // 兼容 .catalog-card（資料庫卡，現行 products.html）與舊 .product-card markup：
    // 任一存在即取，避免卡片 atom 改名後縮圖 title/img 靜默失效。
    const titleEl = card.querySelector('.catalog-card__title, .product-card__title');
    const imgEl = card.querySelector('.catalog-card__media img, .product-card__media img');
    // category 在外層 <li data-category>（非 .catalog-card 上），用 closest 上溯。
    // 同類限定比較的鎖定依據；舊 localStorage 資料無此欄 → lockedCategory 回 '' 視為 UNLOCKED。
    return {
      title: titleEl?.textContent?.trim() || '',
      img: imgEl?.getAttribute('src') || '',
      category: card.closest('[data-category]')?.dataset.category || '',
    };
  }

  /* ===== Public API ===== */

  has(id) {
    return this.items.some((it) => it.id === String(id));
  }

  // 同類限定鎖：集合非空時鎖到第一顆的 category；空集合或舊資料無 category → '' 視為 UNLOCKED。
  get lockedCategory() {
    return this.items.length ? (this.items[0].category || '') : '';
  }

  add(id, meta = {}) {
    const sid = String(id);
    if (this.has(sid)) return this;
    if (this.items.length >= this.maxSlots) {
      // 滿了：閃爍提示（is-full-flash 由 CSS pulse）+ aria-live 朗讀上限告知。
      // 不做 replace-oldest；既有已加入卡仍可移除（移除動作走 has(id) 分支，不進此處）。
      this.el.classList.add('is-full-flash');
      setTimeout(() => this.el.classList.remove('is-full-flash'), 400);
      this._announce(`比較最多 ${this.maxSlots} 項，已達上限。請先移除一項再加入。`);
      return this;
    }
    this.items.push({ id: sid, title: meta.title || '', img: meta.img || '', category: meta.category || '' });
    this._save();
    this._render();
    this._syncCardState(sid, true);
    return this;
  }

  remove(id) {
    const sid = String(id);
    this.items = this.items.filter((it) => it.id !== sid);
    this._save();
    this._render();
    this._syncCardState(sid, false);
    return this;
  }

  clear() {
    const removed = this.items.map((it) => it.id);
    this.items = [];
    this._save();
    this._render();
    removed.forEach((id) => this._syncCardState(id, false));
    return this;
  }

  getItems() {
    return this.items.slice();
  }

  /* ===== Render ===== */

  _render() {
    if (!this.slotsEl) return;

    const slots = [];
    for (let i = 0; i < this.maxSlots; i += 1) {
      const item = this.items[i];
      if (item) {
        // 全部外來值（localStorage 來源，_load 僅 Array.isArray 檢查未消毒）一律走共用 escapeHtml
        // entity-escape，與全站一致。id 先前漏 escape（data-id 內插）→ <img onerror> 破口（9.1）；
        // title/img 先前用 strip-based replace（漏 & 且行為漂移）→ 改 escapeHtml 收斂（9.2）。
        const safeId = escapeHtml(item.id);
        const safeTitle = escapeHtml(item.title);
        const safeImg = escapeHtml(item.img);
        slots.push(
          `<li class="compare-bar__slot" data-id="${safeId}">` +
            (safeImg ? `<img src="${safeImg}" alt="${safeTitle}" width="80" height="80" loading="lazy">` : '<span aria-hidden="true">·</span>') +
            `<button class="compare-bar__slot-remove" type="button" data-id="${safeId}" aria-label="移除${safeTitle}">×</button>` +
          `</li>`
        );
      } else {
        slots.push(`<li class="compare-bar__slot is-empty" aria-hidden="true">+</li>`);
      }
    }
    this.slotsEl.innerHTML = slots.join('');

    // 注入的 slot 縮圖是新 <img loading="lazy">：main.css 對其設 opacity:0，靠 .is-loaded 才淡入。
    // 但分類縮圖多為 cache 命中（complete 即時為真）→ load 事件不再 fire → 永停 opacity:0 = 空白。
    // 對 slots 容器重綁懶載入顯示（bindLazyImages 內對 complete 圖立即補 .is-loaded，不等 load）。
    // 守 typeof 與 products.js:91 同慣例：hook 未掛時靜默跳過。innerHTML 重建後每次 _render 都重綁，
    // 同時覆蓋 fresh-add（原本空白的破口）與 reload（原本靠 module-init 全域掃才正常）兩路徑。
    if (typeof window !== 'undefined' && typeof window.__reapplyLazyReveal === 'function') {
      window.__reapplyLazyReveal(this.slotsEl);
    }

    // 顯示 / 隱藏
    if (this.items.length === 0) {
      this.el.removeAttribute('aria-hidden');
      this.el.setAttribute('hidden', '');
      this.el.classList.remove('is-visible', 'is-full');
    } else {
      this.el.removeAttribute('hidden');
      // 雙 RAF 確保 transition 觸發
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.el.classList.add('is-visible'));
      });
      this.el.classList.toggle('is-full', this.items.length >= this.maxSlots);
    }

    // CTA 數字
    if (this.cta) {
      const baseLabel = this.cta.dataset.baseLabel || this.cta.textContent.replace(/\s*\(\d+\)\s*$/, '').trim();
      this.cta.dataset.baseLabel = baseLabel;
      this.cta.textContent = `${baseLabel} (${this.items.length})`;
    }

    // 同類鎖狀態同步（add/remove/clear 都走 _render → 清空自動解鎖）。
    this._syncLock();
    // 上限封鎖只在「點擊當下」對該鈕加 aria-disabled（見 _bind，避 pointer-events:none
    // 吞掉觸發 flash 的點擊）；此處負責反向：未滿時清掉殘留的 aria-disabled，讓釋出名額後
    // 先前被封的加入鈕復活。只移除、不新增 → 不會造成 pointer-events 鎖死。
    if (this.items.length < this.maxSlots) {
      document.querySelectorAll('[data-add-compare][aria-disabled="true"]').forEach((btn) => {
        if (!btn.classList.contains('button--added')) btn.removeAttribute('aria-disabled');
      });
    }
  }

  _syncCardState(id, selected) {
    document.querySelectorAll(`[data-product-id="${CSS.escape(id)}"]`).forEach((card) => {
      card.classList.toggle('is-selected', selected);
      const btn = card.querySelector('[data-add-compare]');
      if (btn) {
        btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
        // 切換 active 變體 class——讓「已加入」態走 .button--added（teal 實底白字 + 勾 icon），
        // 脫離 .button--outline:hover 的 navy 實底邏輯（修「兩顆已加入長不一樣」的 hover 殘留）。
        btn.classList.toggle('button--added', selected);
        if (selected) {
          btn.dataset.originalLabel = btn.dataset.originalLabel || btn.textContent.trim();
          btn.textContent = '已加入';
        } else if (btn.dataset.originalLabel) {
          btn.textContent = btn.dataset.originalLabel;
        }
      }
    });
  }

  // 把鎖定 category 寫上 <html data-compare-lock>，CSS 據此 muted 跨類「加入比較」按鈕；解鎖時移除屬性。
  _syncLock() {
    const locked = this.lockedCategory;
    if (locked) {
      document.documentElement.setAttribute('data-compare-lock', locked);
    } else {
      document.documentElement.removeAttribute('data-compare-lock');
    }
  }

  // 寫入 aria-live 區，朗讀「無法加入」告知（muted 按鈕不設 disabled/aria-disabled，改以此 + modal role=dialog 告知）。
  _announce(msg) {
    const node = document.querySelector('[data-compare-announce]');
    if (node) node.textContent = msg;
  }
}

const instances = new WeakMap();

function getOrCreate(target) {
  const el = typeof target === 'string' ? document.querySelector(target) : target;
  if (!el) return null;
  let inst = instances.get(el);
  if (!inst) {
    inst = new CompareBarInstance(el);
    instances.set(el, inst);
  }
  return inst;
}

export const CompareBar = {
  init(target) {
    return getOrCreate(target);
  },
  get(target) {
    return getOrCreate(target);
  },
};

// ===== Auto-init =====
function autoInit() {
  document.querySelectorAll('[data-compare-bar]').forEach((el) => {
    if (el.dataset.compareBarBound === '1') return;
    el.dataset.compareBarBound = '1';
    getOrCreate(el);
  });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    autoInit();
  }
}

if (typeof window !== 'undefined') {
  window.CompareBar = CompareBar;
}

export default CompareBar;
